import React, { KeyboardEvent, useRef } from 'react';
import { Button, Thumb, Track } from './styles';

export interface ISegmentOption<T extends string> {
  value: T;
  label: string;
}

interface ISegmentedControlProps<T extends string> {
  options: ISegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  'aria-label': string;
}

/** Abas curtas (ex.: Cronograma / Classificação) no padrão de controle segmentado. */
function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  'aria-label': ariaLabel,
}: ISegmentedControlProps<T>) {
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const index = Math.max(
    0,
    options.findIndex(o => o.value === value),
  );

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    const step = event.key === 'ArrowRight' ? 1 : -1;
    const next = (index + step + options.length) % options.length;
    onChange(options[next].value);
    buttons.current[next]?.focus();
  };

  return (
    <Track
      role="tablist"
      aria-label={ariaLabel}
      $count={options.length}
      onKeyDown={handleKeyDown}
    >
      <Thumb $count={options.length} $index={index} aria-hidden />
      {options.map((option, i) => (
        <Button
          key={option.value}
          ref={el => {
            buttons.current[i] = el;
          }}
          type="button"
          role="tab"
          aria-selected={option.value === value}
          tabIndex={option.value === value ? 0 : -1}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </Button>
      ))}
    </Track>
  );
}

export default SegmentedControl;
